/**
 * Client for the public website support chatbot (POST /api/support-chat).
 *
 * Deliberately NOT built on `apiFetch`: that helper sends cookies
 * (`credentials: "include"`) for the logged-in dashboard. The chatbot is
 * anonymous, so this sends no cookies and no identifiers of any kind -- just
 * the conversation text. Nothing is persisted in the browser either (no
 * localStorage / cookies); the chat lives in component state.
 */
import type { SupportChatMessage, SupportChatResponse } from "@platform/shared";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "http://127.0.0.1:8787";
const REQUEST_TIMEOUT_MS = 30_000;

export type SupportChatErrorKind = "rate_limited" | "unavailable" | "invalid" | "network";

export class SupportChatError extends Error {
  constructor(
    public readonly kind: SupportChatErrorKind,
    message: string,
    public readonly retryAfterSeconds?: number
  ) {
    super(message);
    this.name = "SupportChatError";
  }
}

export async function sendSupportChat(messages: SupportChatMessage[]): Promise<string> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}/api/support-chat`, {
      method: "POST",
      credentials: "omit",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ messages }),
      signal: controller.signal,
    });
  } catch {
    throw new SupportChatError("network", "Couldn't reach the assistant. Check your connection and try again.");
  } finally {
    clearTimeout(timer);
  }

  if (response.ok) {
    const body = (await response.json().catch(() => null)) as Partial<SupportChatResponse> | null;
    if (typeof body?.reply !== "string" || body.reply.length === 0) {
      throw new SupportChatError("unavailable", "The assistant gave an unexpected response. Please try again.");
    }
    return body.reply;
  }

  const body = (await response.json().catch(() => null)) as { error?: { message?: string } } | null;
  const serverMessage = typeof body?.error?.message === "string" ? body.error.message : null;

  if (response.status === 429) {
    const retryAfter = Number(response.headers.get("Retry-After"));
    throw new SupportChatError(
      "rate_limited",
      serverMessage ?? "Too many messages. Please wait a moment and try again.",
      Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : undefined
    );
  }
  if (response.status >= 400 && response.status < 500) {
    throw new SupportChatError("invalid", serverMessage ?? "That message couldn't be sent. Try rephrasing it.");
  }
  throw new SupportChatError("unavailable", serverMessage ?? "The assistant is unavailable right now. Please try again shortly.");
}
